import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Accordion, Col } from 'react-bootstrap'
import DeleteIcon from '../../assets/images/trash.svg?url'
import { Button } from '../Button/Button'
import { EditorState, LexicalEditor } from 'lexical'
import { $generateHtmlFromNodes } from '@lexical/html'
import { WritableLexicalEditor } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { RichTextToolbarPlugin } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { LEXICAL_NODES } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import {
  getEditorStateFromHtml,
  getEditorStateFromText,
  getEditorTextContent,
  isEditorRichText,
} from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import './ExampleBox.scss'

export interface ExampleBoxProps {
  example: string
  onChange: (newExample: string) => void
  onDelete?: () => void
  isOpen: boolean
  toggleOpen: () => void
  shouldReset: boolean
}

export const ExampleBox: React.FC<ExampleBoxProps> = ({ onDelete, isOpen, toggleOpen, example, onChange, shouldReset }) => {
  const editorRef = useRef<LexicalEditor>(null)

  const [initialEditorState, setInitialEditorState] = useState<EditorState>(() => {
    const htmlEditorState = getEditorStateFromHtml(example)
    const isRichText = isEditorRichText(htmlEditorState)
    return isRichText ? htmlEditorState : getEditorStateFromText(example)
  })

  const resetToInitial = useCallback(() => {
    const editor = editorRef.current
    if (!editor || !initialEditorState) {
      return
    }
    editor.setEditorState(initialEditorState)
  }, [initialEditorState])

  useEffect(() => {
    if (shouldReset) {
      resetToInitial()
    }
  }, [shouldReset, resetToInitial])

  useEffect(() => {
    const htmlEditorState = getEditorStateFromHtml(example)
    const isRichText = isEditorRichText(htmlEditorState)
    const newInitialEditorState = isRichText ? htmlEditorState : getEditorStateFromText(example)
    setInitialEditorState(newInitialEditorState)
  }, [example])

  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const isRichText = isEditorRichText(editorState)
      const newExample = isRichText ? $generateHtmlFromNodes(editor, null) : getEditorTextContent(editorState)
      onChange(newExample)
    })
  }

  return (
    <Accordion defaultActiveKey='1' activeKey={isOpen ? '0' : null}>
      <Accordion.Item className='example-box' eventKey='0'>
        <Accordion.Header onClick={toggleOpen} className='label-medium'>
          Example
        </Accordion.Header>
        <Accordion.Body>
          <Col>
            {initialEditorState ? (
              <WritableLexicalEditor
                ref={editorRef}
                className='p-0 template-box-editor'
                editorContainerClassName={'flex-grow-1 pt-2 px-3 pb-4'}
                onChange={handleChange}
                initialEditorState={initialEditorState}
                lexicalNodes={LEXICAL_NODES}
                header={<RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2' />}
              />
            ) : null}
            <div className='d-flex justify-content-end pt-3 pb-2'>
              {onDelete && (
                <Button onClick={onDelete} className='button-small' variant='link'>
                  <div className='item'>
                    <img alt='Delete' src={DeleteIcon} />
                  </div>
                  <i className='bi bi-traash' />
                  <div className='item'>Delete</div>
                </Button>
              )}
            </div>
          </Col>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  )
}
